import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Lock, ArrowLeft, CheckCircle, Truck, MapPin, Info, Copy, Check, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { FLASH_SALE, isDiscountEligible } from '../data/promotions';
import { useToast } from '../hooks/use-toast';

const PAY_PHONE = '562-292-1095';
const PAY_PHONE_RAW = '5622921095';

const PhonePayCard = ({
  brandName,
  brandColor,
  brandRgba,
  logoBg,
  logoText,
  logoTextColor,
  copyKey,
  steps,
  copiedField,
  copyToClipboard,
}) => {
  const isCopied = copiedField === copyKey;
  return (
    <div className="mt-3">
      <div
        className="rounded-lg p-5"
        style={{
          background: 'linear-gradient(135deg, #1A1411 0%, #2A1F18 100%)',
          border: `1px solid ${brandRgba}`,
        }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ backgroundColor: logoBg }}
          >
            <span className="font-extrabold text-xl" style={{ color: logoTextColor }}>
              {logoText}
            </span>
          </div>
          <div>
            <p
              className="text-xs uppercase tracking-wider"
              style={{ color: '#B5A79E', letterSpacing: '0.1em' }}
            >
              Send via
            </p>
            <p className="font-bold text-base" style={{ color: '#F7F3EE' }}>
              {brandName}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => copyToClipboard(PAY_PHONE_RAW, copyKey)}
          data-testid={`copy-${copyKey}`}
          className="w-full rounded-md p-4 text-left transition-all hover:scale-[1.01] active:scale-[0.99]"
          style={{
            backgroundColor: '#0C0A09',
            border: `1px dashed ${isCopied ? 'rgba(34, 197, 94, 0.55)' : brandRgba}`,
            cursor: 'pointer',
          }}
          aria-label={`Tap to copy phone number ${PAY_PHONE}`}
        >
          <p
            className="text-xs uppercase mb-2"
            style={{ color: brandColor, letterSpacing: '0.12em' }}
          >
            Send to phone number
          </p>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p
              className="font-bold text-2xl sm:text-3xl tracking-wide"
              style={{ color: '#F7F3EE', fontFamily: 'monospace' }}
            >
              {PAY_PHONE}
            </p>
            <span
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wide"
              style={{
                backgroundColor: isCopied
                  ? 'rgba(34, 197, 94, 0.15)'
                  : 'rgba(212, 163, 115, 0.12)',
                border: `1px solid ${
                  isCopied ? 'rgba(34, 197, 94, 0.5)' : 'rgba(212, 163, 115, 0.4)'
                }`,
                color: isCopied ? '#86EFAC' : '#D4A373',
              }}
            >
              {isCopied ? (
                <>
                  <Check className="h-3.5 w-3.5" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" /> Tap to Copy
                </>
              )}
            </span>
          </div>
          <p className="text-xs mt-2" style={{ color: '#B5A79E' }}>
            {isCopied
              ? 'Number copied — paste it into the app to send'
              : 'Tap anywhere on this card to copy the number'}
          </p>
        </button>

        <div className="mt-4 space-y-2 text-sm" style={{ color: '#B5A79E' }}>
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <span style={{ color: brandColor }}>{idx + 1}.</span>
              <span>{step}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [copiedField, setCopiedField] = useState('');

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('shipping');
  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // { code, percent }
  const [promoError, setPromoError] = useState('');
  const [formData, setFormData] = useState({
    // Shipping Info
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const copyToClipboard = async (text, fieldKey) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(''), 1800);
    } catch (err) {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(ta);
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(''), 1800);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPaymentMethod) {
      toast({
        title: "Payment method required",
        description: "Please select a payment method to continue.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);

    // Submit order to backend
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const orderData = {
        customer: formData,
        items: cartItems,
        subtotal: subtotal,
        shipping: shipping,
        tax: tax,
        total: total,
        paymentMethod: selectedPaymentMethod,
        deliveryMethod: deliveryMethod,
        ...(activeDiscount && discount > 0 && {
          promoCode: promoApplied ? promoApplied.code : 'FLASH15',
          discount: parseFloat(discount.toFixed(2)),
        }),
      };

      const response = await fetch(`${BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const result = await response.json();
        setIsProcessing(false);
        setCompletedOrder({
          orderId: result.order_id,
          total,
          paymentMethod: selectedPaymentMethod,
          deliveryMethod,
          email: formData.email,
        });
        setOrderComplete(true);
        clearCart();
        // Bring the success screen into view
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error('Failed to submit order');
      }
    } catch (error) {
      setIsProcessing(false);
      toast({
        title: "Error submitting order",
        description: "Please try again or contact support.",
        variant: "destructive"
      });
    }
  };

  const subtotal = getCartTotal();
  const isLocalMeetup = deliveryMethod === 'local_meetup';
  const eligibleSubtotal = cartItems
    .filter(isDiscountEligible)
    .reduce((sum, item) => sum + item.price * item.quantity, 0);
  const activeDiscount = promoApplied
    ? { label: `Code ${promoApplied.code}`, percent: promoApplied.percent }
    : FLASH_SALE.active
      ? { label: FLASH_SALE.label, percent: FLASH_SALE.percent }
      : null;
  const discount = activeDiscount ? eligibleSubtotal * activeDiscount.percent : 0;
  const discountedSubtotal = subtotal - discount;
  const shipping = isLocalMeetup ? 0 : (discountedSubtotal > 150 ? 0 : 9.99);
  const tax = isLocalMeetup ? 0 : discountedSubtotal * 0.08;
  const total = discountedSubtotal + shipping + tax;

  const applyPromo = async () => {
    setPromoError('');
    const code = promoInput.trim();
    if (!code) {
      setPromoError('Enter a code first');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/promo/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        setPromoError('Invalid code');
        return;
      }
      const data = await res.json();
      setPromoApplied({ code: data.code, percent: data.percent });
    } catch {
      setPromoError('Could not check code, please try again');
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoInput('');
    setPromoError('');
  };

  if (cartItems.length === 0 && !orderComplete) {
    return (
      <div className="min-h-screen pt-20" style={{ backgroundColor: "#0C0A09" }}>
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#F7F3EE] mb-4">Your cart is empty</h2>
            <p className="text-[#B5A79E] mb-8">Add some products before checking out</p>
            <Button onClick={() => navigate('/products')} className="bg-[#D4A373] hover:bg-[#E6CC98]">
              Browse Products
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (orderComplete && completedOrder) {
    const { orderId, total: orderTotal, paymentMethod, deliveryMethod: dm, email } = completedOrder;
    const shortId = orderId ? orderId.slice(0, 8).toUpperCase() : '';
    const memo = `Order #${shortId}`;

    const paymentDetails = {
      zelle: {
        label: 'Zelle',
        rows: [
          { k: 'Phone Number', v: PAY_PHONE, copyKey: 'zelle-phone-success' },
        ],
        note: 'Open your bank app → tap Zelle → send by phone number to 562-292-1095. Include your order ID in the memo.',
      },
      cash_app: {
        label: 'Cash App',
        rows: [
          { k: 'Phone Number', v: PAY_PHONE, copyKey: 'cashapp-phone-success' },
        ],
        note: 'Open Cash App → Pay → enter 562-292-1095 → send the total with your order ID in the note.',
      },
      paypal: {
        label: 'PayPal',
        rows: [
          { k: 'Phone Number', v: PAY_PHONE, copyKey: 'paypal-phone-success' },
        ],
        note: 'Open PayPal → Send → Mobile Number → 562-292-1095. Send as Friends & Family with your order ID in the note.',
      },
      apple_pay: {
        label: 'Apple Pay / Apple Cash',
        rows: [
          { k: 'Phone Number', v: '310-702-3495', copyKey: 'ap-phone' },
        ],
      },
      cash: {
        label: 'Cash (Local Meetup)',
        rows: [],
        note: 'Bring exact cash to the meetup. We\'ll DM you to coordinate a time and location in Norwalk, CA 90650.',
      },
    }[paymentMethod] || { label: paymentMethod, rows: [] };

    const CopyBtn = ({ value, fieldKey }) => (
      <button
        type="button"
        onClick={() => copyToClipboard(value, fieldKey)}
        data-testid={`copy-${fieldKey}`}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wide transition-all hover:scale-[1.03]"
        style={{
          backgroundColor: copiedField === fieldKey ? 'rgba(34, 197, 94, 0.15)' : 'rgba(212, 163, 115, 0.12)',
          border: `1px solid ${copiedField === fieldKey ? 'rgba(34, 197, 94, 0.5)' : 'rgba(212, 163, 115, 0.4)'}`,
          color: copiedField === fieldKey ? '#86EFAC' : '#D4A373',
        }}
      >
        {copiedField === fieldKey ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copiedField === fieldKey ? 'Copied' : 'Copy'}
      </button>
    );

    return (
      <div className="min-h-screen pt-20 pb-12" style={{ backgroundColor: '#0C0A09' }}>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto" data-testid="order-confirmation">
            {/* Success Hero */}
            <div className="text-center mb-8">
              <div
                className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
                style={{
                  backgroundColor: 'rgba(34, 197, 94, 0.12)',
                  border: '2px solid rgba(34, 197, 94, 0.4)',
                }}
              >
                <CheckCircle className="h-10 w-10" style={{ color: '#86EFAC' }} />
              </div>
              <h1
                className="text-4xl sm:text-5xl font-bold mb-3"
                style={{ fontFamily: 'Playfair Display, serif', color: '#F7F3EE' }}
              >
                Order Submitted
              </h1>
              <p className="text-base" style={{ color: '#B5A79E' }}>
                Thanks for your order. Now let's complete payment so we can ship it out.
              </p>
            </div>

            {/* Order ID Card */}
            <div
              className="rounded-lg p-5 mb-5 flex items-center justify-between gap-4"
              style={{ backgroundColor: '#1A1513', border: '1px solid #362B25' }}
            >
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#B5A79E' }}>
                  Your Order ID
                </p>
                <p
                  data-testid="order-id-display"
                  className="text-2xl font-bold tracking-wider truncate"
                  style={{ color: '#F7F3EE', fontFamily: 'monospace' }}
                >
                  #{shortId}
                </p>
              </div>
              <CopyBtn value={shortId} fieldKey="order-id" />
            </div>

            {/* Step 1: Amount */}
            <div
              className="rounded-lg p-5 mb-5"
              style={{
                background: 'linear-gradient(135deg, #1A1513 0%, #221A14 100%)',
                border: '1px solid rgba(212, 163, 115, 0.35)',
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                  style={{ backgroundColor: '#D4A373', color: '#0C0A09' }}
                >
                  1
                </div>
                <h2 className="text-lg font-bold" style={{ color: '#F7F3EE' }}>
                  Send this exact amount
                </h2>
              </div>
              <div
                className="rounded p-4 flex items-center justify-between gap-4"
                style={{ backgroundColor: '#0C0A09', border: '1px dashed rgba(212, 163, 115, 0.4)' }}
              >
                <div>
                  <p className="text-xs uppercase tracking-widest" style={{ color: '#B5A79E' }}>
                    Total Due
                  </p>
                  <p
                    data-testid="confirmation-total"
                    className="text-3xl font-bold"
                    style={{ color: '#D4A373' }}
                  >
                    ${orderTotal.toFixed(2)}
                  </p>
                </div>
                <CopyBtn value={orderTotal.toFixed(2)} fieldKey="total-amount" />
              </div>
            </div>

            {/* Step 2: Payment Details */}
            <div
              className="rounded-lg p-5 mb-5"
              style={{
                background: 'linear-gradient(135deg, #1A1513 0%, #221A14 100%)',
                border: '1px solid rgba(212, 163, 115, 0.35)',
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                  style={{ backgroundColor: '#D4A373', color: '#0C0A09' }}
                >
                  2
                </div>
                <h2 className="text-lg font-bold" style={{ color: '#F7F3EE' }}>
                  Send via {paymentDetails.label}
                </h2>
              </div>

              <div className="space-y-3">
                {paymentDetails.note && (() => {
                  const noteStyles = {
                    zelle: { bg: 'rgba(109, 30, 212, 0.10)', border: 'rgba(109, 30, 212, 0.4)', badgeBg: '#6D1ED4', badgeFg: '#FFFFFF', glyph: 'z' },
                    cash_app: { bg: 'rgba(0, 214, 50, 0.08)', border: 'rgba(0, 214, 50, 0.35)', badgeBg: '#00D632', badgeFg: '#0C0A09', glyph: '$' },
                    paypal: { bg: 'rgba(0, 156, 222, 0.10)', border: 'rgba(0, 156, 222, 0.4)', badgeBg: '#003087', badgeFg: '#FFFFFF', glyph: 'P' },
                    cash: { bg: 'rgba(46, 184, 46, 0.10)', border: 'rgba(46, 184, 46, 0.4)', badgeBg: '#2EB82E', badgeFg: '#FFFFFF', glyph: '💵' },
                  };
                  const noteStyle = noteStyles[paymentMethod] || noteStyles.cash_app;
                  return (
                    <div
                      className="rounded p-4 flex items-start gap-3"
                      style={{
                        backgroundColor: noteStyle.bg,
                        border: `1px solid ${noteStyle.border}`,
                      }}
                    >
                      <div
                        className="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center font-extrabold text-base"
                        style={{ backgroundColor: noteStyle.badgeBg, color: noteStyle.badgeFg }}
                        aria-hidden="true"
                      >
                        {noteStyle.glyph}
                      </div>
                      <p
                        className="text-sm sm:text-base leading-relaxed"
                        style={{ color: '#F7F3EE' }}
                      >
                        {paymentDetails.note}
                      </p>
                    </div>
                  );
                })()}
                {paymentDetails.rows.map((row) => (
                  <div
                    key={row.copyKey}
                    className="rounded p-4 flex items-center justify-between gap-4"
                    style={{ backgroundColor: '#0C0A09', border: '1px solid #362B25' }}
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-xs uppercase tracking-widest mb-1"
                        style={{ color: '#B5A79E' }}
                      >
                        {row.k}
                      </p>
                      <p
                        className="text-base font-semibold truncate select-all"
                        style={{ color: '#F7F3EE', fontFamily: 'monospace' }}
                      >
                        {row.v}
                      </p>
                    </div>
                    <CopyBtn value={row.v} fieldKey={row.copyKey} />
                  </div>
                ))}

                {(() => {
                  const smsPhones = {
                    zelle: '5622921095',
                    cash_app: '5622921095',
                    paypal: '5622921095',
                    apple_pay: '3107023495',
                  };
                  const smsTo = smsPhones[paymentMethod];
                  if (!smsTo) return null;
                  const smsBody = encodeURIComponent(
                    `Hi, I just sent $${orderTotal.toFixed(2)} for order #${shortId} via ${paymentDetails.label}.`
                  );
                  return (
                    <a
                      href={`sms:${smsTo}?&body=${smsBody}`}
                      data-testid="sms-payment-link"
                      className="rounded p-4 flex items-center justify-between gap-3 transition-all hover:scale-[1.01] active:scale-[0.99]"
                      style={{
                        background: 'linear-gradient(135deg, #D4A373 0%, #B5895F 100%)',
                        border: '1px solid rgba(212, 163, 115, 0.6)',
                        boxShadow: '0 0 24px rgba(212, 163, 115, 0.15)',
                        color: '#0C0A09',
                        textDecoration: 'none',
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className="text-xs uppercase tracking-widest mb-1 font-bold"
                          style={{ color: '#0C0A09', opacity: 0.7 }}
                        >
                          One-Tap Confirmation
                        </p>
                        <p className="text-base sm:text-lg font-bold" style={{ color: '#0C0A09' }}>
                          Text us once you've sent it
                        </p>
                        <p className="text-xs mt-1" style={{ color: '#0C0A09', opacity: 0.75 }}>
                          Opens your texts pre-filled with your order details
                        </p>
                      </div>
                      <div
                        className="flex-shrink-0 px-4 py-2.5 rounded-md font-bold text-sm uppercase tracking-wide"
                        style={{
                          backgroundColor: '#0C0A09',
                          color: '#D4A373',
                          border: '1px solid #0C0A09',
                        }}
                      >
                        Open Texts
                      </div>
                    </a>
                  );
                })()}
              </div>
            </div>

            {/* Step 3: Memo */}
            <div
              className="rounded-lg p-5 mb-5"
              style={{
                background: 'linear-gradient(135deg, #1A1513 0%, #221A14 100%)',
                border: '1px solid rgba(212, 163, 115, 0.35)',
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                  style={{ backgroundColor: '#D4A373', color: '#0C0A09' }}
                >
                  3
                </div>
                <h2 className="text-lg font-bold" style={{ color: '#F7F3EE' }}>
                  Add this in the payment note
                </h2>
              </div>
              <div
                className="rounded p-4 flex items-center justify-between gap-4"
                style={{ backgroundColor: '#0C0A09', border: '1px dashed rgba(212, 163, 115, 0.4)' }}
              >
                <p
                  data-testid="confirmation-memo"
                  className="text-base font-semibold select-all"
                  style={{ color: '#F7F3EE', fontFamily: 'monospace' }}
                >
                  {memo}
                </p>
                <CopyBtn value={memo} fieldKey="memo" />
              </div>
              <p className="text-xs mt-3" style={{ color: '#B5A79E' }}>
                Including this helps us match your payment to your order instantly.
              </p>
            </div>

            {/* Local meetup callout */}
            {dm === 'local_meetup' && (
              <div
                className="rounded-lg p-5 mb-5 flex items-start gap-3"
                style={{
                  backgroundColor: 'rgba(212, 163, 115, 0.08)',
                  border: '1px solid rgba(212, 163, 115, 0.4)',
                }}
              >
                <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#D4A373' }} />
                <div>
                  <p className="text-sm font-semibold mb-1" style={{ color: '#F7F3EE' }}>
                    Local Meetup — Norwalk, CA 90650
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B5A79E' }}>
                    After payment is received, reach out by email or DM us on Discord to coordinate a pickup time and location.
                  </p>
                </div>
              </div>
            )}

            {/* Email reminder */}
            <div
              className="rounded-lg p-4 mb-6 flex items-start gap-3"
              style={{ backgroundColor: '#1A1513', border: '1px solid #362B25' }}
            >
              <Mail className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#D4A373' }} />
              <p className="text-sm leading-relaxed" style={{ color: '#B5A79E' }}>
                We'll email <span style={{ color: '#F7F3EE', fontWeight: 600 }}>{email}</span> as soon as your payment is confirmed and your order is on the way.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate('/products')}
                data-testid="continue-shopping-btn"
                className="flex-1 bg-[#D4A373] hover:bg-[#E6CC98] text-[#0C0A09] font-bold h-12"
              >
                Continue Shopping
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                data-testid="back-to-home-btn"
                className="flex-1 h-12 border-[#362B25] text-[#F7F3EE] hover:bg-[#1A1513] hover:text-[#F7F3EE]"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: "#0C0A09" }}>
      <div className="container mx-auto px-4 py-12">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/products')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Continue Shopping
        </Button>

        {/* Header */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#F7F3EE] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Checkout</h1>
        <p className="text-base sm:text-lg mb-6" style={{ color: '#B5A79E' }}>
          Don't worry — placing this order will <span style={{ color: '#FCD34D', fontWeight: 700 }}>NOT</span> charge your card.
        </p>

        {/* How This Works - 3 Step Guide */}
        <div
          data-testid="checkout-disclaimer"
          className="relative mb-8 rounded-xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #1A1513 0%, #221A14 100%)',
            border: '2px solid #D4A373',
            boxShadow: '0 0 32px rgba(212, 163, 115, 0.18)',
          }}
        >
          {/* Header strip */}
          <div
            className="px-5 sm:px-8 py-4 sm:py-5 text-center"
            style={{
              background: 'linear-gradient(135deg, #D4A373 0%, #C78C50 100%)',
            }}
          >
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight"
              style={{ color: '#0C0A09', fontFamily: 'Playfair Display, serif' }}
            >
              How This Works
            </h2>
            <p
              className="text-sm sm:text-base font-semibold mt-1"
              style={{ color: '#0C0A09', opacity: 0.85 }}
            >
              3 simple steps · No charge until you send payment
            </p>
          </div>

          {/* Steps */}
          <div className="p-5 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-4">
            {/* Step 1 */}
            <div className="flex md:flex-col items-start md:items-center gap-4 md:gap-3 md:text-center">
              <div
                className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold"
                style={{
                  backgroundColor: '#D4A373',
                  color: '#0C0A09',
                }}
              >
                1
              </div>
              <div>
                <p
                  className="text-lg sm:text-xl font-bold mb-1"
                  style={{ color: '#F7F3EE' }}
                >
                  Fill out the form
                </p>
                <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#B5A79E' }}>
                  Tell us your shipping info and which payment method you'll use.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex md:flex-col items-start md:items-center gap-4 md:gap-3 md:text-center">
              <div
                className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold"
                style={{
                  backgroundColor: '#D4A373',
                  color: '#0C0A09',
                }}
              >
                2
              </div>
              <div>
                <p
                  className="text-lg sm:text-xl font-bold mb-1"
                  style={{ color: '#F7F3EE' }}
                >
                  Press <span style={{ color: '#D4A373' }}>"Submit Order"</span>
                </p>
                <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#B5A79E' }}>
                  This sends us your order. <span style={{ color: '#FCD34D', fontWeight: 700 }}>No money is taken yet.</span>
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex md:flex-col items-start md:items-center gap-4 md:gap-3 md:text-center">
              <div
                className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold"
                style={{
                  backgroundColor: '#D4A373',
                  color: '#0C0A09',
                }}
              >
                3
              </div>
              <div>
                <p
                  className="text-lg sm:text-xl font-bold mb-1"
                  style={{ color: '#F7F3EE' }}
                >
                  Send the payment yourself
                </p>
                <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#B5A79E' }}>
                  Open Zelle, Cash App, or Apple Pay on your phone and send the total. We'll show you exactly where.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom callout */}
          <div
            className="px-5 sm:px-8 py-4 text-center"
            style={{
              backgroundColor: 'rgba(252, 211, 77, 0.08)',
              borderTop: '1px solid rgba(252, 211, 77, 0.25)',
            }}
          >
            <p
              className="text-base sm:text-lg font-bold"
              style={{ color: '#FCD34D' }}
            >
              ⚠️ This website does NOT charge your card. You pay us yourself in step 3.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Shipping Information */}
              <Card className="border border-[#362B25]">
                <CardContent className="p-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#F7F3EE] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Where do we ship it?</h2>
                  <p className="text-sm sm:text-base mb-6" style={{ color: '#B5A79E' }}>
                    Step 1 — fill in your address so we know where to send your order.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-base font-semibold text-[#D4A373] mb-2">
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="h-12"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-base font-semibold text-[#D4A373] mb-2">
                          Email *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="h-12"
                        />
                      </div>
                      <div>
                        <label className="block text-base font-semibold text-[#D4A373] mb-2">
                          Phone *
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="h-12"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-base font-semibold text-[#D4A373] mb-2">
                        Address *
                      </label>
                      <Input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        className="h-12"
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2 md:col-span-2">
                        <label className="block text-base font-semibold text-[#D4A373] mb-2">
                          City *
                        </label>
                        <Input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          className="h-12"
                        />
                      </div>
                      <div>
                        <label className="block text-base font-semibold text-[#D4A373] mb-2">
                          State *
                        </label>
                        <Input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          required
                          className="h-12"
                        />
                      </div>
                      <div>
                        <label className="block text-base font-semibold text-[#D4A373] mb-2">
                          ZIP *
                        </label>
                        <Input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleChange}
                          required
                          className="h-12"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Delivery Method */}
              <Card className="border border-[#362B25]">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <Truck className="h-6 w-6 text-[#D4A373]" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#F7F3EE]" style={{ fontFamily: 'Playfair Display, serif' }}>How should we get it to you?</h2>
                  </div>
                  <p className="text-sm sm:text-base mb-6" style={{ color: '#B5A79E' }}>
                    Pick shipping or local pickup.
                  </p>

                  <div className="space-y-4">
                    {/* Standard Shipping Option */}
                    <div
                      data-testid="delivery-shipping-option"
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        deliveryMethod === 'shipping'
                          ? 'border-[#D4A373] bg-[#1A1411]'
                          : 'border-[#362B25] hover:border-[#5A4A3F]'
                      }`}
                      onClick={() => setDeliveryMethod('shipping')}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 flex-shrink-0 ${
                          deliveryMethod === 'shipping' ? 'border-[#D4A373]' : 'border-[#5A4A3F]'
                        }`}>
                          {deliveryMethod === 'shipping' && (
                            <div className="w-3 h-3 rounded-full bg-[#D4A373]"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-[#F7F3EE]">Standard Shipping</p>
                            <p className="text-sm font-semibold text-[#D4A373]">
                              {subtotal > 150 ? 'FREE' : '$9.99'}
                            </p>
                          </div>
                          <p className="text-sm text-[#B5A79E] mt-1">
                            Discreet packaging delivered to your address. Free on orders over $150.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Local Meetup Option */}
                    <div
                      data-testid="delivery-local-meetup-option"
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        deliveryMethod === 'local_meetup'
                          ? 'border-[#D4A373] bg-[#1A1411]'
                          : 'border-[#362B25] hover:border-[#5A4A3F]'
                      }`}
                      onClick={() => setDeliveryMethod('local_meetup')}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 flex-shrink-0 ${
                          deliveryMethod === 'local_meetup' ? 'border-[#D4A373]' : 'border-[#5A4A3F]'
                        }`}>
                          {deliveryMethod === 'local_meetup' && (
                            <div className="w-3 h-3 rounded-full bg-[#D4A373]"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-[#F7F3EE]">Local Meetup Pickup</p>
                            <p className="text-sm font-semibold text-[#D4A373]">FREE</p>
                          </div>
                          <p className="text-sm text-[#B5A79E] mt-1">
                            Skip shipping fees and sales tax by picking up your order in person.
                          </p>

                          {deliveryMethod === 'local_meetup' && (
                            <div
                              data-testid="local-meetup-info"
                              className="mt-4 p-4 rounded-lg"
                              style={{ backgroundColor: 'rgba(212, 163, 115, 0.08)', border: '1px solid rgba(212, 163, 115, 0.3)' }}
                            >
                              <div className="flex items-start gap-3">
                                <MapPin className="h-5 w-5 text-[#D4A373] mt-0.5 flex-shrink-0" />
                                <div className="space-y-2">
                                  <p className="text-sm font-semibold text-[#F7F3EE]">
                                    Pickup Location: Norwalk, CA 90650
                                  </p>
                                  <p className="text-sm text-[#B5A79E] leading-relaxed">
                                    After your order is placed, please reach out by email or send a direct message through our Discord community to coordinate a convenient time and meetup location. We'll confirm the details with you personally.
                                  </p>
                                  <p className="text-xs text-[#D4A373] font-medium pt-1">
                                    No shipping charges · No sales tax · Fast & flexible
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Information */}
              <Card className="border border-[#362B25]">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <CreditCard className="h-6 w-6 text-[#D4A373]" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#F7F3EE]" style={{ fontFamily: 'Playfair Display, serif' }}>How will you pay?</h2>
                  </div>
                  <p className="text-sm sm:text-base text-[#B5A79E] mb-6">
                    Pick how you'll send the money <span style={{ color: '#FCD34D', fontWeight: 700 }}>after</span> you submit this order. Nothing is charged here.
                  </p>

                  <div className="space-y-4">
                    {/* Zelle Option */}
                    <div 
                      data-testid="payment-zelle-option"
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPaymentMethod === 'zelle' 
                          ? 'border-[#D4A373] bg-[#1A1411]' 
                          : 'border-[#362B25] hover:border-[#5A4A3F]'
                      }`}
                      onClick={() => setSelectedPaymentMethod('zelle')}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 flex-shrink-0 ${
                          selectedPaymentMethod === 'zelle' ? 'border-[#D4A373]' : 'border-[#5A4A3F]'
                        }`}>
                          {selectedPaymentMethod === 'zelle' && (
                            <div className="w-3 h-3 rounded-full bg-[#D4A373]"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className="w-6 h-6 rounded-md flex items-center justify-center"
                              style={{ backgroundColor: '#6D1ED4' }}
                              aria-hidden="true"
                            >
                              <span className="font-extrabold text-sm" style={{ color: '#FFFFFF' }}>z</span>
                            </div>
                            <p className="font-bold text-lg sm:text-xl text-[#F7F3EE]">Zelle</p>
                          </div>
                          <p className="text-sm text-[#B5A79E] mb-2">From your bank app</p>
                          {selectedPaymentMethod === 'zelle' && (
                            <PhonePayCard
                              brandName="Zelle"
                              brandColor="#6D1ED4"
                              brandRgba="rgba(109, 30, 212, 0.45)"
                              logoBg="#6D1ED4"
                              logoText="z"
                              logoTextColor="#FFFFFF"
                              copyKey="zelle-phone"
                              steps={[
                                'Open your bank app and tap Zelle',
                                <>Choose <strong style={{ color: '#F7F3EE' }}>Send by phone number</strong> and enter <strong style={{ color: '#F7F3EE' }}>562-292-1095</strong></>,
                                'Add your order ID in the memo and send the total',
                              ]}
                              copiedField={copiedField}
                              copyToClipboard={copyToClipboard}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Cash App Option */}
                    <div
                      data-testid="payment-cashapp-option"
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPaymentMethod === 'cash_app'
                          ? 'border-[#D4A373] bg-[#1A1411]'
                          : 'border-[#362B25] hover:border-[#5A4A3F]'
                      }`}
                      onClick={() => setSelectedPaymentMethod('cash_app')}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 flex-shrink-0 ${
                          selectedPaymentMethod === 'cash_app' ? 'border-[#D4A373]' : 'border-[#5A4A3F]'
                        }`}>
                          {selectedPaymentMethod === 'cash_app' && (
                            <div className="w-3 h-3 rounded-full bg-[#D4A373]"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className="w-6 h-6 rounded-md flex items-center justify-center font-extrabold text-base"
                              style={{ backgroundColor: '#00D632', color: '#0C0A09' }}
                              aria-hidden="true"
                            >
                              $
                            </div>
                            <p className="font-bold text-lg sm:text-xl text-[#F7F3EE]">Cash App</p>
                          </div>
                          <p className="text-sm text-[#B5A79E] mb-2">From the Cash App on your phone</p>
                          {selectedPaymentMethod === 'cash_app' && (
                            <PhonePayCard
                              brandName="Cash App"
                              brandColor="#00D632"
                              brandRgba="rgba(0, 214, 50, 0.45)"
                              logoBg="#00D632"
                              logoText="$"
                              logoTextColor="#0C0A09"
                              copyKey="cashapp-phone"
                              steps={[
                                'Open Cash App and tap the Pay tab',
                                <>Enter <strong style={{ color: '#F7F3EE' }}>562-292-1095</strong> in the recipient field</>,
                                'Type the order total, add your order ID in the note, and send',
                              ]}
                              copiedField={copiedField}
                              copyToClipboard={copyToClipboard}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* PayPal Option */}
                    <div
                      data-testid="payment-paypal-option"
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPaymentMethod === 'paypal'
                          ? 'border-[#D4A373] bg-[#1A1411]'
                          : 'border-[#362B25] hover:border-[#5A4A3F]'
                      }`}
                      onClick={() => setSelectedPaymentMethod('paypal')}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 flex-shrink-0 ${
                          selectedPaymentMethod === 'paypal' ? 'border-[#D4A373]' : 'border-[#5A4A3F]'
                        }`}>
                          {selectedPaymentMethod === 'paypal' && (
                            <div className="w-3 h-3 rounded-full bg-[#D4A373]"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className="w-6 h-6 rounded-md flex items-center justify-center"
                              style={{ backgroundColor: '#003087' }}
                              aria-hidden="true"
                            >
                              <span className="font-extrabold text-sm" style={{ color: '#FFFFFF' }}>P</span>
                            </div>
                            <p className="font-bold text-lg sm:text-xl text-[#F7F3EE]">PayPal</p>
                          </div>
                          <p className="text-sm text-[#B5A79E] mb-2">From the PayPal app</p>
                          {selectedPaymentMethod === 'paypal' && (
                            <PhonePayCard
                              brandName="PayPal"
                              brandColor="#009CDE"
                              brandRgba="rgba(0, 156, 222, 0.45)"
                              logoBg="#003087"
                              logoText="P"
                              logoTextColor="#FFFFFF"
                              copyKey="paypal-phone"
                              steps={[
                                'Open PayPal and tap Send',
                                <>Choose <strong style={{ color: '#F7F3EE' }}>Mobile Number</strong> and enter <strong style={{ color: '#F7F3EE' }}>562-292-1095</strong></>,
                                'Send the total as Friends & Family with your order ID in the note',
                              ]}
                              copiedField={copiedField}
                              copyToClipboard={copyToClipboard}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Apple Pay Option */}
                    <div
                      data-testid="payment-applepay-option"
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPaymentMethod === 'apple_pay'
                          ? 'border-[#D4A373] bg-[#1A1411]'
                          : 'border-[#362B25] hover:border-[#5A4A3F]'
                      }`}
                      onClick={() => setSelectedPaymentMethod('apple_pay')}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 flex-shrink-0 ${
                          selectedPaymentMethod === 'apple_pay' ? 'border-[#D4A373]' : 'border-[#5A4A3F]'
                        }`}>
                          {selectedPaymentMethod === 'apple_pay' && (
                            <div className="w-3 h-3 rounded-full bg-[#D4A373]"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" style={{ color: '#F7F3EE' }} aria-hidden="true">
                              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                            </svg>
                            <p className="font-bold text-lg sm:text-xl text-[#F7F3EE]">Apple Pay / Apple Cash</p>
                          </div>
                          <p className="text-sm text-[#B5A79E] mb-2">From Messages or the Wallet app on iPhone</p>
                          {selectedPaymentMethod === 'apple_pay' && (
                            <div className="mt-3">
                              <div
                                className="rounded-lg p-5"
                                style={{
                                  background: 'linear-gradient(135deg, #1A1411 0%, #2A1F18 100%)',
                                  border: '1px solid rgba(212, 163, 115, 0.3)'
                                }}
                              >
                                <div className="flex items-center gap-3 mb-4">
                                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4A373' }}>
                                    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="#0C0A09" aria-hidden="true">
                                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                                    </svg>
                                  </div>
                                  <div>
                                    <p className="text-xs uppercase tracking-wider" style={{ color: '#B5A79E', letterSpacing: '0.1em' }}>Send via</p>
                                    <p className="font-bold text-base" style={{ color: '#F7F3EE' }}>Apple Pay · Apple Cash</p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard('3107023495', 'ap-phone-checkout')}
                                  data-testid="copy-ap-phone-checkout"
                                  className="w-full rounded p-4 text-left transition-all hover:scale-[1.01] active:scale-[0.99]"
                                  style={{
                                    backgroundColor: '#0C0A09',
                                    border: `1px dashed ${copiedField === 'ap-phone-checkout' ? 'rgba(34, 197, 94, 0.55)' : 'rgba(212, 163, 115, 0.45)'}`,
                                    cursor: 'pointer',
                                  }}
                                  aria-label="Tap to copy Apple Pay phone number"
                                >
                                  <p className="text-xs uppercase mb-2" style={{ color: '#D4A373', letterSpacing: '0.12em' }}>Send to phone number</p>
                                  <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <p
                                      data-testid="apple-pay-phone"
                                      className="font-bold text-2xl sm:text-3xl tracking-wide"
                                      style={{ color: '#F7F3EE', fontFamily: 'monospace' }}
                                    >
                                      310-702-3495
                                    </p>
                                    <span
                                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wide"
                                      style={{
                                        backgroundColor: copiedField === 'ap-phone-checkout' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(212, 163, 115, 0.12)',
                                        border: `1px solid ${copiedField === 'ap-phone-checkout' ? 'rgba(34, 197, 94, 0.5)' : 'rgba(212, 163, 115, 0.4)'}`,
                                        color: copiedField === 'ap-phone-checkout' ? '#86EFAC' : '#D4A373',
                                      }}
                                    >
                                      {copiedField === 'ap-phone-checkout' ? (
                                        <><Check className="h-3.5 w-3.5" /> Copied</>
                                      ) : (
                                        <><Copy className="h-3.5 w-3.5" /> Tap to Copy</>
                                      )}
                                    </span>
                                  </div>
                                  <p className="text-xs mt-2" style={{ color: '#B5A79E' }}>
                                    {copiedField === 'ap-phone-checkout'
                                      ? 'Number copied — paste it into Apple Cash to send'
                                      : 'Tap anywhere on this card to copy the number'}
                                  </p>
                                </button>
                                <div className="mt-4 space-y-2 text-sm" style={{ color: '#B5A79E' }}>
                                  <div className="flex items-start gap-2">
                                    <span style={{ color: '#D4A373' }}>1.</span>
                                    <span>Open the Messages or Wallet app on your iPhone</span>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <span style={{ color: '#D4A373' }}>2.</span>
                                    <span>Send the order amount to <strong style={{ color: '#F7F3EE' }}>310-702-3495</strong> via Apple Cash</span>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <span style={{ color: '#D4A373' }}>3.</span>
                                    <span>Add your order ID in the message note</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Cash Option */}
                    <div
                      data-testid="payment-cash-option"
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPaymentMethod === 'cash'
                          ? 'border-[#D4A373] bg-[#1A1411]'
                          : 'border-[#362B25] hover:border-[#5A4A3F]'
                      }`}
                      onClick={() => setSelectedPaymentMethod('cash')}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 flex-shrink-0 ${
                          selectedPaymentMethod === 'cash' ? 'border-[#D4A373]' : 'border-[#5A4A3F]'
                        }`}>
                          {selectedPaymentMethod === 'cash' && (
                            <div className="w-3 h-3 rounded-full bg-[#D4A373]"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className="w-7 h-6 rounded-md flex items-center justify-center"
                              style={{
                                background: 'linear-gradient(135deg, #1F7A1F 0%, #2EB82E 100%)',
                                border: '1px solid #2EB82E',
                              }}
                              aria-hidden="true"
                            >
                              <span className="font-extrabold text-xs" style={{ color: '#FFFFFF', letterSpacing: '0.05em' }}>$$</span>
                            </div>
                            <p className="font-bold text-lg sm:text-xl text-[#F7F3EE]">Cash</p>
                            <span
                              className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded ml-1"
                              style={{
                                backgroundColor: 'rgba(46, 184, 46, 0.15)',
                                color: '#86EFAC',
                                border: '1px solid rgba(46, 184, 46, 0.4)',
                              }}
                            >
                              Local Meetup
                            </span>
                          </div>
                          <p className="text-sm text-[#B5A79E] mb-2">Pay in person when we meet up</p>
                          {selectedPaymentMethod === 'cash' && (
                            <div className="mt-3">
                              <div
                                className="rounded-xl overflow-hidden"
                                style={{
                                  background: 'linear-gradient(135deg, #1F7A1F 0%, #2EB82E 100%)',
                                  padding: '14px',
                                  boxShadow: '0 0 32px rgba(46, 184, 46, 0.35)',
                                }}
                              >
                                <div
                                  className="px-4 py-1.5 mb-3 text-center"
                                  style={{ backgroundColor: '#0C0A09', borderRadius: '8px' }}
                                >
                                  <p
                                    className="text-xs uppercase tracking-[0.25em] font-bold"
                                    style={{ color: '#86EFAC' }}
                                  >
                                    Cash · Pay on Pickup
                                  </p>
                                </div>
                                <div
                                  className="rounded-lg p-4"
                                  style={{ backgroundColor: '#0C0A09' }}
                                >
                                  <div className="text-center mb-3">
                                    <div className="text-5xl mb-2">💵</div>
                                    <p
                                      className="text-base font-bold mb-1"
                                      style={{ color: '#F7F3EE' }}
                                    >
                                      Bring your total in cash
                                    </p>
                                    <p
                                      className="text-sm leading-relaxed"
                                      style={{ color: '#B5A79E' }}
                                    >
                                      Hand it over at the meetup spot. No tax, no shipping, no app to download.
                                    </p>
                                  </div>
                                  <div
                                    className="rounded p-3 text-center"
                                    style={{
                                      backgroundColor: 'rgba(252, 211, 77, 0.1)',
                                      border: '1px solid rgba(252, 211, 77, 0.4)',
                                    }}
                                  >
                                    <p
                                      className="text-xs font-bold uppercase tracking-wider mb-1"
                                      style={{ color: '#FCD34D' }}
                                    >
                                      ⚠️ Local Meetup Only
                                    </p>
                                    <p
                                      className="text-xs"
                                      style={{ color: '#F7F3EE' }}
                                    >
                                      For Norwalk, CA 90650 pickup. Please also select "Local Meetup Pickup" above.
                                    </p>
                                  </div>
                                </div>
                                <p
                                  className="text-xs text-center mt-3 font-semibold"
                                  style={{ color: '#FFFFFF' }}
                                >
                                  We'll DM you to coordinate time & exact location
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedPaymentMethod && (
                    <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(252, 211, 77, 0.1)', border: '1px solid rgba(252, 211, 77, 0.4)' }}>
                      <p className="text-base sm:text-lg font-semibold leading-relaxed" style={{ color: '#F7F3EE' }}>
                        <span style={{ color: '#FCD34D', fontWeight: 800 }}>Reminder:</span> When you press the big button below, it just <span style={{ color: '#FCD34D', fontWeight: 800 }}>sends us your order</span>. You'll then send the money yourself using the details we'll show you.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Big clear submit button */}
              <div className="space-y-3">
                <p className="text-center text-sm sm:text-base font-semibold" style={{ color: '#FCD34D' }}>
                  ⚠️ This will NOT charge you. It just sends us your order.
                </p>
                <Button
                  type="submit"
                  disabled={isProcessing}
                  data-testid="place-order-button"
                  className="w-full text-[#0C0A09] font-extrabold transition-all hover:scale-[1.01]"
                  style={{
                    height: '80px',
                    fontSize: '20px',
                    backgroundColor: '#D4A373',
                    boxShadow: '0 0 32px rgba(212, 163, 115, 0.35)',
                    lineHeight: '1.2',
                  }}
                >
                  {isProcessing
                    ? 'Sending order…'
                    : (
                      <span className="flex flex-col items-center justify-center leading-tight">
                        <span className="text-xl sm:text-2xl font-extrabold uppercase tracking-wide">Submit Order</span>
                        <span className="text-sm sm:text-base font-semibold mt-0.5 opacity-80">No charge — you'll pay next · ${total.toFixed(2)}</span>
                      </span>
                    )}
                </Button>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="border border-[#362B25] sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-[#F7F3EE] mb-6">Order Summary</h2>
                
                {/* Cart Items */}
                <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex justify-between items-start pb-4 border-b border-gray-200">
                      <div className="flex-1">
                        <p className="font-semibold text-[#F7F3EE]">{item.name}</p>
                        <p className="text-sm text-[#B5A79E]">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-semibold text-[#F7F3EE]">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                  
                  {/* Ultimate Starter Pack Bonus (when subtotal >= $100) */}
                  {subtotal >= 100 && (
                    <div className="pt-4 mt-2">
                      <div className="p-3 rounded" style={{ backgroundColor: 'rgba(212, 163, 115, 0.1)', border: '1px dashed #D4A373' }}>
                        <p className="text-xs font-bold uppercase mb-3 text-center" style={{ color: '#D4A373', letterSpacing: '0.05em' }}>
                          🎁 ULTIMATE STARTER PACK - FREE!
                        </p>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <span>💉</span>
                              <p className="text-sm font-medium" style={{ color: '#F7F3EE' }}>Premium Syringes (5x)</p>
                            </div>
                            <p className="text-sm font-bold" style={{ color: '#D4A373' }}>FREE</p>
                          </div>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <span>💧</span>
                              <p className="text-sm font-medium" style={{ color: '#F7F3EE' }}>BAC Water (3ml)</p>
                            </div>
                            <p className="text-sm font-bold" style={{ color: '#D4A373' }}>FREE</p>
                          </div>
                        </div>
                        <p className="text-xs text-center mt-2 pt-2" style={{ color: '#D4A373', borderTop: '1px solid rgba(212, 163, 115, 0.2)' }}>
                          💰 You Save $13.00!
                        </p>
                        <p className="text-[10px] text-center mt-1 uppercase tracking-wider font-semibold" style={{ color: '#FCD34D' }}>
                          ⚠️ First-time buyers only
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Promo Code */}
                <div
                  className="mb-4 p-3 rounded-md"
                  style={{ backgroundColor: '#0C0A09', border: '1px dashed #362B25' }}
                  data-testid="promo-code-block"
                >
                  {promoApplied ? (
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#86EFAC' }}>
                          ✓ Promo Applied
                        </p>
                        <p className="text-sm font-bold" style={{ color: '#F7F3EE' }} data-testid="promo-applied-label">
                          {promoApplied.code} — {Math.round(promoApplied.percent * 100)}% off singles
                        </p>
                        {FLASH_SALE.active && (
                          <p className="text-[11px] mt-0.5" style={{ color: '#B5A79E' }} data-testid="promo-replaces-flash-note">
                            Replaces the 15% flash sale (discounts don't stack)
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={removePromo}
                        data-testid="promo-remove-btn"
                        className="text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded"
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid #362B25',
                          color: '#B5A79E',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <label
                        className="text-[10px] uppercase tracking-widest font-bold block mb-1.5"
                        style={{ color: '#B5A79E' }}
                      >
                        Have a code?
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoInput}
                          onChange={(e) => { setPromoInput(e.target.value); setPromoError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromo(); } }}
                          placeholder="Enter code"
                          maxLength={30}
                          autoComplete="off"
                          autoCapitalize="none"
                          spellCheck={false}
                          data-testid="promo-code-input"
                          className="flex-1 px-3 py-2 rounded text-sm focus:outline-none"
                          style={{
                            backgroundColor: '#1A1513',
                            border: '1px solid #362B25',
                            color: '#F7F3EE',
                          }}
                        />
                        <button
                          type="button"
                          onClick={applyPromo}
                          data-testid="promo-apply-btn"
                          className="px-4 py-2 rounded text-xs font-bold uppercase tracking-wide transition-all hover:scale-[1.02]"
                          style={{
                            background: 'linear-gradient(135deg, #D4A373 0%, #B5895F 100%)',
                            color: '#0C0A09',
                          }}
                        >
                          Apply
                        </button>
                      </div>
                      {promoError && (
                        <p className="text-xs mt-1.5" style={{ color: '#FCA5A5' }} data-testid="promo-error">
                          {promoError}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Pricing Breakdown */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-[#D4A373]">
                    <span>Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  {activeDiscount && discount > 0 && (
                    <div data-testid="promo-discount-row">
                      <div className="flex justify-between" style={{ color: '#86EFAC' }}>
                        <span data-testid="discount-label">{activeDiscount.label}</span>
                        <span data-testid="discount-amount">−${discount.toFixed(2)}</span>
                      </div>
                      <p className="text-[11px] mt-0.5" style={{ color: '#8A7B70' }}>
                        Kits, bundles &amp; BAC water not discounted
                      </p>
                    </div>
                  )}
                  <div className="flex justify-between text-[#D4A373]">
                    <span>{isLocalMeetup ? 'Local Meetup' : 'Shipping'}</span>
                    <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  {!isLocalMeetup && (
                    <div className="flex justify-between text-[#D4A373]">
                      <span>Tax</span>
                      <span>${tax.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-[#362B25] pt-3 flex justify-between text-lg font-bold text-[#F7F3EE]">
                    <span>Total</span>
                    <span className="text-[#D4A373]">${total.toFixed(2)}</span>
                  </div>
                </div>

                {isLocalMeetup ? (
                  <p className="text-xs text-[#D4A373] text-center">
                    ✓ No shipping · No tax · Norwalk, CA pickup
                  </p>
                ) : shipping > 0 ? (
                  <p className="text-xs text-[#B5A79E] text-center">
                    Free shipping on orders over $150
                  </p>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
